import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'

import { Button } from '@/components/button/button'

import { getCaseEoirDetailQuery } from '@/lib/queries/business/v1/case/eoir/queries'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { cn } from '@/lib/utils'

import { CaseAlerts } from '../case-detail/ui/case-alerts/case-alerts'
import { ClientPortalArea } from '../ui/client-portal-area/client-portal-area'
import { ApplicantCaseEoirDetailClose as Close } from './ui/close/close'
import { ApplicantCaseEoirDetailCourtInformation as CourtInfo } from './ui/court-information/court-information'
import { ApplicantCaseEoirDetailError as Error } from './ui/error/error'
import { ApplicantCaseEoirDetailHeader as Header } from './ui/header/header'
import { ApplicantCaseEoirDetailHistory as History } from './ui/history/history'
import { useFindTargetCaseEoir } from './ui/hooks/use-find-target-case'
import { ApplicantCaseEoirDetailLoading as Loading } from './ui/loading/loading'
import { ApplicantCaseEoirDetailNextHearing as NextHearing } from './ui/next-hearing/next-hearing'

export const ApplicantCaseEoirDetailPage = () => {
  const { applicantId, caseNumber } = useParams({
    from: '/_app/applicants/$applicantId/cases-eoir/$caseNumber',
  })

  const { targetCase } = useFindTargetCaseEoir()
  const caseId = targetCase?.case_id

  const { data: dataUser } = useQuery(getUserQuery)
  const { data, error, isLoading } = useQuery(getCaseEoirDetailQuery(caseNumber))

  return (
    <div key={caseNumber} className={cn('relative', 'w-[384px] overflow-auto border-l pb-20 shadow-md')}>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <Error error={error} />
      ) : (
        <>
          <Header className="mb-6 ml-6 mr-2 mt-5" />
          <History className="mx-6" statusList={data?.statusList ?? []} />
          <div className="mb-6 mt-5 h-2 w-full bg-secondary" />
          <h3 className={cn('mb-5 ml-[25px]', 'text-[16px] font-[500] leading-[24px]')}>Case Detail</h3>
          <div className={cn('mx-[25px] mb-6', 'flex flex-col gap-3')}>
            <NextHearing caseDetail={data} />
            <CourtInfo caseDetail={data} />
          </div>
          <h3 className={cn('mb-5 ml-[25px]', 'text-[16px] font-[500] leading-[24px]')}>Track Important Dates</h3>
          <div className={cn('mx-[25px]')}>
            <CaseAlerts isEoir />
          </div>

          <div className={cn('mt-6')}>
            <ClientPortalArea
              linkButton={
                <Link
                  to={
                    dataUser?.hasSubscription
                      ? '/client-portal/eoir/internal/$applicantId/$caseId'
                      : '/client-portal/eoir/internal/sample'
                  }
                  target="_blank"
                  rel="noreferrer"
                  className={cn('w-full')}
                  params={{
                    applicantId: applicantId ? `${applicantId}` : '',
                    caseId: caseId ? `${caseId}` : '',
                  }}
                >
                  <Button className={cn('flex-1', 'w-full')} asChild>
                    <div className={cn('flex gap-x-2')}>
                      Client Portal
                      <ArrowRightIcon size={16} />
                    </div>
                  </Button>
                </Link>
              }
            />
          </div>
        </>
      )}

      <Close className={cn('absolute right-3 top-3')} />
    </div>
  )
}
